export type RuntimeMode =
  | 'telegram-mini-app'
  | 'telegram-login-browser'
  | 'chrome-browser'
  | 'safari-browser'
  | 'unknown-browser';

export interface RuntimeCapabilities {
  mode: RuntimeMode;
  canUseSignedTelegramAuth: boolean;
  canUseBrowserGeolocation: boolean;
  canUseClipboard: boolean;
  canOpenExternalMaps: boolean;
  canUseNativeTelegramBack: boolean;
  canUseTelegramCloudStorage: boolean;
}

export interface OpenExternalUrlOptions {
  tryTelegram?: boolean;
  target?: string;
  features?: string;
}

export interface OpenExternalUrlResult {
  ok: boolean;
  url: string;
  mode: RuntimeMode;
  fallbackReason?: string;
}

export interface RuntimeSupportProfile {
  modeLabel: string;
  supportPercent: number;
  supportedGroups: readonly string[];
  telegramOnlyGroups: readonly string[];
  demoCopy: string;
}

export const runtimeSupportMatrix = {
  telegram: {
    modeLabel: 'Telegram Mini App',
    supportPercent: 100,
    supportedGroups: [
      'Public search and maps',
      'Google Maps navigation handoff',
      'Favorites and worklists',
      'Hazards, weather, and field intelligence',
      'Route preview',
      'Signed company records',
      'Forms, JSAs, and timesheets',
      'Fleet, policies, and training',
      'Bot notifications and meetings',
      'AI/RAG company knowledge',
    ],
    telegramOnlyGroups: [],
    demoCopy: 'Full signed app experience with private records, company workflows, notifications, and AI.',
  },
  browserDemo: {
    modeLabel: 'Chrome Free Demo Mode',
    supportPercent: 50,
    supportedGroups: [
      'Public search and maps',
      'Google Maps navigation handoff',
      'Favorites and worklists',
      'Hazards, weather, and field intelligence',
      'Route preview',
    ],
    telegramOnlyGroups: [
      'Signed company records',
      'Forms, JSAs, and timesheets',
      'Fleet, policies, and training',
      'Bot notifications and meetings',
      'AI/RAG company knowledge',
    ],
    demoCopy: 'Free browser preview for public navigation workflows. Open in Telegram for signed company features.',
  },
} as const satisfies Record<string, RuntimeSupportProfile>;

const getTelegramWebApp = (): any | null => {
  if (typeof window === 'undefined') return null;
  return (window as any).Telegram?.WebApp || null;
};

const getLaunchParamInitData = (): string => {
  if (typeof window === 'undefined') return '';
  const params = new URLSearchParams();
  const append = (raw: string) => {
    const normalized = raw.replace(/^[?#]/, '');
    if (!normalized) return;
    try {
      new URLSearchParams(normalized).forEach((value, key) => {
        if (!params.has(key)) params.set(key, value);
      });
    } catch {
      // Ignore malformed launch params.
    }
  };
  append(window.location.search);
  append(window.location.hash);
  return params.get('tgWebAppData') || '';
};

const hasTelegramLoginSession = (): boolean => {
  try {
    return Boolean(window.sessionStorage?.getItem('tg_auth_user'));
  } catch {
    return false;
  }
};

export const getRuntimeMode = (): RuntimeMode => {
  if (typeof window === 'undefined') return 'unknown-browser';

  const webApp = getTelegramWebApp();
  const initData = String(webApp?.initData || getLaunchParamInitData() || '');
  if (initData) return 'telegram-mini-app';
  if (hasTelegramLoginSession()) return 'telegram-login-browser';

  const userAgent = navigator.userAgent || '';
  const isChrome = /Chrome|CriOS|Chromium/i.test(userAgent) && !/Edg|OPR|SamsungBrowser/i.test(userAgent);
  if (isChrome) return 'chrome-browser';

  const isSafari = /Safari/i.test(userAgent) && !/Chrome|CriOS|Chromium|Android|Edg|OPR/i.test(userAgent);
  if (isSafari) return 'safari-browser';

  return 'unknown-browser';
};

export const getRuntimeCapabilities = (): RuntimeCapabilities => {
  const mode = getRuntimeMode();
  const webApp = getTelegramWebApp();
  const hasSignedTelegramAuth = mode === 'telegram-mini-app';

  return {
    mode,
    canUseSignedTelegramAuth: hasSignedTelegramAuth,
    canUseBrowserGeolocation: typeof navigator !== 'undefined' && Boolean(navigator.geolocation),
    canUseClipboard: typeof navigator !== 'undefined' && Boolean(navigator.clipboard?.writeText),
    canOpenExternalMaps:
      typeof window !== 'undefined' &&
      (typeof window.open === 'function' || typeof webApp?.openLink === 'function'),
    canUseNativeTelegramBack: hasSignedTelegramAuth && typeof webApp?.BackButton?.show === 'function',
    canUseTelegramCloudStorage: hasSignedTelegramAuth && Boolean(webApp?.CloudStorage),
  };
};

export const getRuntimeSupportProfile = (mode: RuntimeMode): RuntimeSupportProfile => {
  if (mode === 'telegram-mini-app') return runtimeSupportMatrix.telegram;
  return runtimeSupportMatrix.browserDemo;
};

export const openExternalUrl = (
  url: string,
  options: OpenExternalUrlOptions = {}
): OpenExternalUrlResult => {
  const capabilities = getRuntimeCapabilities();
  const webApp = getTelegramWebApp();
  const target = options.target || '_blank';
  const features = options.features || 'noopener,noreferrer';

  if (options.tryTelegram !== false && capabilities.canUseSignedTelegramAuth && typeof webApp?.openLink === 'function') {
    try {
      webApp.openLink(url, { try_instant_view: false });
      return { ok: true, url, mode: capabilities.mode };
    } catch (error) {
      console.warn('[Runtime] Telegram openLink failed, falling back to browser open', error);
    }
  }

  try {
    const opened = window.open(url, target, features);
    if (opened) {
      try { opened.opener = null; } catch {}
      return { ok: true, url, mode: capabilities.mode };
    }
    return {
      ok: false,
      url,
      mode: capabilities.mode,
      fallbackReason: 'Popup was blocked. Use the visible link or copy it.',
    };
  } catch (error) {
    return {
      ok: false,
      url,
      mode: capabilities.mode,
      fallbackReason: error instanceof Error ? error.message : 'Unable to open link',
    };
  }
};
